import { Button } from '../../../shared/ui'
import { useContractTemplates, useApproveTemplate, useFirmaSignatories } from './contractApi'

const STATE_TONES: Record<string, string> = {
  draft:    'bg-surface-container-low text-on-surface-variant border border-outline-variant',
  reviewed: 'bg-amber-500/15 text-amber-200 border border-amber-400/30',
  approved: 'bg-sky-500/15 text-sky-200 border border-sky-400/30',
  active:   'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30',
  archived: 'bg-surface-container text-on-surface-variant',
}

export function TemplatesAdminPage() {
  const templatesQ = useContractTemplates()
  const signatoriesQ = useFirmaSignatories()
  const approve = useApproveTemplate()

  const rows = templatesQ.data?.rows ?? []
  const signatories = signatoriesQ.data?.rows ?? []

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Vertragsvorlagen</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Entwürfe werden hochgeladen, geprüft und freigegeben. Nur freigegebene Vorlagen können verwendet werden.
        </p>
      </header>

      {/* Signatories — show CEO assignment per Firma */}
      <section className="rounded-2xl border border-outline-variant/40 bg-surface-container/40 p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant mb-3">
          Geschäftsführer-Zuordnung
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['GmbH', 'Schulcatering GmbH'].map(firma => {
            const ceos = signatories.filter(s => s.firma === firma)
            return (
              <div key={firma} className="rounded-xl bg-surface-container/40 border border-outline-variant/30 p-3">
                <div className="text-sm font-medium">{firma}</div>
                <div className="text-xs text-on-surface-variant mt-1 space-y-0.5">
                  {ceos.length === 0 && <div>(keine Geschäftsführer hinterlegt)</div>}
                  {ceos.map(s => (
                    <div key={s.role}>
                      <strong>{s.role === 'ceo1' ? 'CEO 1' : 'CEO 2'}:</strong> {s.person_name}
                      {s.person_email && <span className="ml-1 opacity-75">({s.person_email})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Templates list */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant">Vorlagen</h2>
          <Button variant="primary" disabled title="Upload-UI folgt — bitte aktuell direkt in Blob hochladen + ops.ContractTemplate-Row anlegen.">
            + Neue Vorlage hochladen (geplant)
          </Button>
        </div>

        {templatesQ.isPending ? (
          <p className="text-sm text-on-surface-variant">Lade…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-outline-variant/40 bg-surface-container/30 p-8 text-center">
            <p className="text-on-surface-variant">Noch keine Vorlagen hochgeladen.</p>
            <p className="text-xs text-on-surface-variant mt-2">
              v1: Seed-Skript <code>_seed_placeholder_template.cjs</code> in functions/intranet/ erzeugt eine
              Platzhalter-Vorlage. Echte Vorlage später über diese Seite hochladen.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-outline-variant/40 divide-y divide-outline-variant/30">
            {rows.map(t => (
              <div key={t.id} className="flex items-center gap-4 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.label}</div>
                  <div className="text-xs text-on-surface-variant truncate">
                    v{t.version}
                    {t.firma && <> · {t.firma}</>}
                    {t.anstellungsart && <> · {t.anstellungsart}</>}
                    {t.requires_ceo2 && <> · 2 CEOs erforderlich</>}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATE_TONES[t.template_state] ?? STATE_TONES.draft}`}>
                  {t.template_state}
                </span>
                {t.template_state !== 'active' && t.template_state !== 'archived' && (
                  <Button variant="secondary" onClick={() => approve.mutate(t.id)} disabled={approve.isPending}>
                    Freigeben
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
